import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import ViewFeedbackModal from "./ViewFeedbackModal";
import { toast } from "react-toastify";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import TablePaginationFooter from "components/common/TablePaginationFooter";

const FEELING_OPTIONS = [
  { key: "LIKE", label: "\u{1F44D} LIKE" },
  { key: "DISLIKE", label: "\u{1F44E} DISLIKE" },
  { key: "HAPPY", label: "\u{1F60A} HAPPY" },
  { key: "SAD", label: "\u{1F614} SAD" },
];

export default function FeedbacksTable({
  feedbacks,
  getFeedback,
  deleteFeedback,
  page,
  pageSize,
  setPage,
  setPageSize,
  totalPages,
  totalElements,
}) {
  const [loading, setLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const formatText = (text, end) => `${text.slice(0, end)}...`;
  const FEELING_MAP = FEELING_OPTIONS.reduce((acc, item) => {
    acc[item.key] = item.label;
    return acc;
  }, {});

  const handleViewFeedback = async (id) => {
    setShowModal(true);
    setLoading(true);
    try {
      const res = await getFeedback(id);
      setSelectedFeedback(res);
    } catch (error) {
      toast.error(error, { theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (id) => {
    setFeedbackToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteFeedback(feedbackToDelete);
    } catch (error) {
      console.log(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      accessorKey: "userId",
      header: "User",
      cell: ({ getValue }) => {
        const text = getValue();
        if (!text) {
          return (
            <span
              className="text-500 d-inline-block"
              style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}
            >
              None
            </span>
          );
        }
        const displayText = text.length > 10 ? formatText(text, 15) : text;
        return (
          <span
            className="text-500 d-inline-block"
            style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}
          >
            {displayText}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "subject",
      header: "Subject",
      cell: ({ getValue }) => {
        const text = getValue() || "No description provided";
        const displayText = text.length > 25 ? formatText(text, 25) : text;
        return <h6 className="mb-0 text-900 fs-10">{displayText}</h6>;
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "feeling",
      header: "Feeling",
      cell: ({ getValue }) => {
        const value = getValue();
        const label = FEELING_MAP[value] || value || "—";
        const style = {
          backgroundColor: value ? "#e8f7e3" : "#fbe9e7",
          color: value ? "#2e7d32" : "#c62828",
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "0.7rem",
          border: value ? "1px solid #d5ebd0" : "1px solid #f5c6cb",
          cursor: "default",
          fontWeight: 600,
        };
        return <span style={style}>{label}</span>;
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "content",
      header: "Content",
      cell: ({ getValue }) => {
        const text = getValue() || "No description provided";
        const displayText = text.length > 25 ? formatText(text, 25) : text;
        return (
          <span
            className="d-inline-block"
            style={{
              color: "#343a40",
              fontSize: "0.75rem",
              fontWeight: 500,
              lineHeight: 1.4,
            }}
          >
            {displayText}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "attachments",
      header: "Attachments",
      cell: ({ getValue }) => {
        const attachments = getValue() || [];
        const count = attachments.length;
        return (
          <span
            className="d-flex align-items-center justify-content-center gap-1 text-900"
            style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}
          >
            <FontAwesomeIcon icon="paperclip" />{" "}
            <span>{count}</span>
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Guard privilege={PRIV.VIEW_FEEDBACKS}>
                <Dropdown.Item onClick={() => handleViewFeedback(item.id)}>
                  View
                </Dropdown.Item>
              </Guard>
              <Guard privilege={PRIV.DELETE_FEEDBACKS}>
                <Dropdown.Item
                  onClick={() => confirmDelete(item.id)}
                  className="text-danger"
                >
                  Delete
                </Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: feedbacks,
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-300 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap fs-12"
          tableProps={{
            bordered: true,
            striped: true,
            className: "mb-0 overflow-hidden",
          }}
        />
        <div className="mt-3">
          <TablePaginationFooter
            page={page}
            totalPages={totalPages || 0}
            totalElements={totalElements || 0}
            onPrev={
              page > 0 && totalElements > 0
                ? () => setPage((prev) => Math.max(prev - 1, 0))
                : undefined
            }
            onNext={
              page < totalPages - 1 && totalElements > 0
                ? () => setPage((prev) => prev + 1)
                : undefined
            }
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            pageSizeOptions={[10, 20, 30, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>

      <ViewFeedbackModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedFeedback(null);
        }}
        feedback={selectedFeedback}
        loading={loading}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        itemName="feedback"
        loading={isDeleting}
      />
    </>
  );
}

